import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Mail,
  Phone,
  Send,
  Loader2,
  CheckCircle2,
  Sparkles,
  RotateCcw,
  Pencil,
  X,
} from 'lucide-react'
import axios from 'axios'
import { companiesApi } from '@/services/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Badge } from '@/components/ui/badge'
import { useSetBreadcrumbLabel } from '@/contexts/BreadcrumbContext'

interface OutreachEmailData {
  rendered_email: string
  rendered_email_html: string
  variables: Record<string, string>
  recipient: { email: string | null; phone: string | null; name: string | null }
  from_email?: string | null
  sent: boolean
  sent_at: string | null
  template?: {
    id: string | null
    name: string | null
    role_label: string | null
    is_default: boolean | null
  } | null
  selection_reasoning?: {
    analysis_role: string | null
    determined_role: string | null
    product_categories: string[] | null
    openai_summary: string | null
    key_points: string[] | null
    match_type: string | null
  } | null
}

// Color palette for AI variables (muted, professional colors)
const VARIABLE_COLORS: Record<string, { bg: string; text: string; bgLight: string }> = {
  companyName: {
    bg: 'bg-blue-100 dark:bg-blue-900/40',
    text: 'text-blue-700 dark:text-blue-300',
    bgLight: 'bg-blue-50 dark:bg-blue-900/20',
  },
  callToAction: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/40',
    text: 'text-emerald-700 dark:text-emerald-300',
    bgLight: 'bg-emerald-50 dark:bg-emerald-900/20',
  },
  specificBenefit: {
    bg: 'bg-amber-100 dark:bg-amber-900/40',
    text: 'text-amber-700 dark:text-amber-300',
    bgLight: 'bg-amber-50 dark:bg-amber-900/20',
  },
  personalizedIntro: {
    bg: 'bg-purple-100 dark:bg-purple-900/40',
    text: 'text-purple-700 dark:text-purple-300',
    bgLight: 'bg-purple-50 dark:bg-purple-900/20',
  },
}

const DEFAULT_COLOR = {
  bg: 'bg-gray-100 dark:bg-gray-800',
  text: 'text-gray-700 dark:text-gray-300',
  bgLight: 'bg-gray-50 dark:bg-gray-800/50',
}

function getVariableColor(key: string) {
  return VARIABLE_COLORS[key] || DEFAULT_COLOR
}

/**
 * Splits text into parts, marking AI-generated variable values for highlighting.
 */
function splitByAiVariables(
  text: string,
  variables: Record<string, string>
): Array<{ text: string; isAi: boolean; varName?: string }> {
  const entries = Object.entries(variables)
    .filter(([, v]) => v != null && String(v).trim() !== '')
    .map(([k, v]) => ({ key: k, value: String(v) }))
    .sort((a, b) => b.value.length - a.value.length)

  if (entries.length === 0) return [{ text, isAi: false }]

  const ranges: Array<{ start: number; end: number; varName: string }> = []
  for (const { key, value } of entries) {
    let pos = 0
    while (true) {
      const idx = text.indexOf(value, pos)
      if (idx === -1) break
      ranges.push({ start: idx, end: idx + value.length, varName: key })
      pos = idx + value.length
    }
  }
  ranges.sort((a, b) => a.start - b.start)

  // Merge overlapping ranges (keep first varName)
  const merged: Array<{ start: number; end: number; varName: string }> = []
  for (const r of ranges) {
    if (merged.length && r.start <= merged[merged.length - 1].end) {
      merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, r.end)
    } else {
      merged.push({ ...r })
    }
  }

  const result: Array<{ text: string; isAi: boolean; varName?: string }> = []
  let last = 0
  for (const { start, end, varName } of merged) {
    if (start > last) {
      result.push({ text: text.slice(last, start), isAi: false })
    }
    result.push({ text: text.slice(start, end), isAi: true, varName })
    last = end
  }
  if (last < text.length) {
    result.push({ text: text.slice(last), isAi: false })
  }
  return result.length ? result : [{ text, isAi: false }]
}

function HighlightedEmailPreview({
  text,
  variables,
}: {
  text: string
  variables: Record<string, string>
}) {
  const parts = splitByAiVariables(text, variables)

  return (
    <div className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">
      {parts.map((part, i) => {
        if (part.isAi && part.varName) {
          const color = getVariableColor(part.varName)
          return (
            <span
              key={i}
              className={`rounded px-0.5 ${color.bgLight} ${color.text}`}
            >
              {part.text}
            </span>
          )
        }
        return <span key={i}>{part.text}</span>
      })}
    </div>
  )
}

export default function CompanyOutreachPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const setBreadcrumbLabel = useSetBreadcrumbLabel()
  const [emailBody, setEmailBody] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [reasoningOpen, setReasoningOpen] = useState(false)

  const { data, isLoading, error } = useQuery<OutreachEmailData>({
    queryKey: ['company', id, 'outreach-email'],
    queryFn: () => companiesApi.getOutreachEmail(id!),
    enabled: !!id,
  })

  const { data: company } = useQuery({
    queryKey: ['company', id],
    queryFn: () => companiesApi.get(id!),
    enabled: !!id,
  })

  useEffect(() => {
    if (data?.rendered_email != null) {
      setEmailBody(data.rendered_email)
    }
  }, [data?.rendered_email])

  useEffect(() => {
    if (company?.name) {
      setBreadcrumbLabel('companyName', company.name)
    }
    return () => setBreadcrumbLabel('companyName', undefined)
  }, [company?.name, setBreadcrumbLabel])

  const sendOutreachMutation = useMutation({
    mutationFn: () =>
      companiesApi.sendOutreach(id!, { email_body: emailBody || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', id] })
      queryClient.invalidateQueries({ queryKey: ['company', id, 'outreach-email'] })
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (error || !data) {
    const msg = axios.isAxiosError(error)
      ? error.response?.data?.detail || error.message
      : error instanceof Error
        ? error.message
        : 'Failed to load outreach'
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate(`/companies/${id}`)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Company
        </Button>
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">{msg}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/companies/${id}`)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              Outreach Email
            </h1>
            <p className="text-sm text-muted-foreground">
              Full email content with AI-generated personalization
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => sendOutreachMutation.mutate()}
            disabled={
              data.sent ||
              !data.recipient?.email ||
              sendOutreachMutation.isPending
            }
          >
            {sendOutreachMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : data.sent ? (
              <CheckCircle2 className="mr-2 h-4 w-4" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            {data.sent ? 'Email Sent' : 'Send Email'}
          </Button>
        </div>
      </div>

      {/* Email preview card */}
      <Card className="overflow-hidden">
        <CardHeader className="border-b bg-muted/30 pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Mail className="h-5 w-5" />
            Email Preview
          </CardTitle>
          <div className="mt-2 grid gap-1 text-sm text-muted-foreground">
            <div>
              <span className="font-medium text-foreground">From:</span>{' '}
              {data.from_email || 'Not configured'}
            </div>
            {(data.template?.name || data.template?.role_label) && (
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">Template:</span>
                <Badge variant="secondary" className="font-normal">
                  {data.template.name}
                  {data.template.role_label && (
                    <span className="ml-1 text-muted-foreground">
                      ({data.template.role_label})
                    </span>
                  )}
                </Badge>
              </div>
            )}
            {(data.selection_reasoning?.analysis_role ||
              data.selection_reasoning?.determined_role ||
              data.selection_reasoning?.match_type) && (
              <Collapsible
                open={reasoningOpen}
                onOpenChange={setReasoningOpen}
                className="mt-1"
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-1 text-left font-medium text-foreground hover:underline"
                  >
                    {reasoningOpen ? (
                      <ChevronDown className="h-4 w-4 shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0" />
                    )}
                    Why this template?
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="mt-2 space-y-1.5 rounded-md border bg-background/50 p-3 text-xs">
                    {data.selection_reasoning.analysis_role && (
                      <div>
                        <span className="font-medium">Website identified as:</span>{' '}
                        {data.selection_reasoning.analysis_role}
                      </div>
                    )}
                    {data.selection_reasoning.determined_role && (
                      <div>
                        <span className="font-medium">Mapped to template role:</span>{' '}
                        {data.selection_reasoning.determined_role}
                      </div>
                    )}
                    {data.selection_reasoning.match_type && (
                      <div>
                        <span className="font-medium">Match:</span>{' '}
                        {data.selection_reasoning.match_type === 'exact'
                          ? 'Exact match'
                          : data.selection_reasoning.match_type ===
                              'default_fallback'
                            ? 'Default template fallback'
                            : data.selection_reasoning.match_type ===
                                'first_active'
                              ? 'First active template'
                              : 'Template'}
                      </div>
                    )}
                    {data.selection_reasoning.openai_summary && (
                      <div className="pt-1">
                        <span className="font-medium">Website summary:</span>{' '}
                        <span className="text-muted-foreground">
                          {data.selection_reasoning.openai_summary}
                        </span>
                      </div>
                    )}
                    {Array.isArray(
                      data.selection_reasoning.product_categories
                    ) &&
                      data.selection_reasoning.product_categories.length >
                        0 && (
                        <div>
                          <span className="font-medium">
                            Product categories:
                          </span>{' '}
                          <span className="text-muted-foreground">
                            {data.selection_reasoning.product_categories.join(
                              ', '
                            )}
                          </span>
                        </div>
                      )}
                    {Array.isArray(data.selection_reasoning.key_points) &&
                      data.selection_reasoning.key_points.length > 0 && (
                        <div>
                          <span className="font-medium">Key points:</span>{' '}
                          <span className="text-muted-foreground">
                            {data.selection_reasoning.key_points.join(', ')}
                          </span>
                        </div>
                      )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
            {data.sent_at && (
              <div>
                <span className="font-medium text-foreground">Sent:</span>{' '}
                {new Date(data.sent_at).toLocaleString()}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            {/* AI fields legend with color coding */}
            {Object.keys(data.variables).length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">Personalized Content</span>
                {Object.entries(data.variables).map(([key, value]) => {
                  const color = getVariableColor(key)
                  return (
                    <TooltipProvider key={key}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${color.bg} ${color.text}`}
                          >
                            <Sparkles className="h-3 w-3" />
                            {key}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs text-xs">{value}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )
                })}
              </div>
            )}

            {/* Email content header with actions */}
            <div className="flex items-center justify-end">
              <div className="flex gap-2">
                {!data.sent && emailBody !== data.rendered_email && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEmailBody(data.rendered_email)}
                  >
                    <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                    Reset
                  </Button>
                )}
                {!data.sent && (
                  <Button
                    variant={isEditing ? 'outline' : 'ghost'}
                    size="sm"
                    onClick={() => setIsEditing(!isEditing)}
                  >
                    {isEditing ? (
                      <>
                        <X className="mr-1.5 h-3.5 w-3.5" />
                        Done
                      </>
                    ) : (
                      <>
                        <Pencil className="mr-1.5 h-3.5 w-3.5" />
                        Edit
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* Email content: highlighted preview or editable textarea */}
            <div className="rounded-lg border bg-muted/20 p-4">
              {isEditing ? (
                <Textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  placeholder="Email content..."
                  className="min-h-[400px] resize-y border-0 bg-transparent p-0 font-sans text-sm leading-relaxed shadow-none focus-visible:ring-0"
                />
              ) : (
                <HighlightedEmailPreview
                  text={emailBody || data.rendered_email}
                  variables={data.variables}
                />
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Phone call script - coming soon */}
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Phone className="h-5 w-5" />
            Phone Call Script
            <span className="rounded bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
              Coming Soon
            </span>
          </CardTitle>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              AI-generated phone call scripts and talking points will be
              available here.
            </p>
          </CardContent>
        </CardHeader>
      </Card>
    </div>
  )
}
